import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Navigation, Store, Phone, Globe, Clock, Star, Loader, List, Map, Search, Eye, EyeOff, Target } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { getSmokerEmoji } from '../static/SmokerEmoji';
import { DispensaryDetailModal } from './DispensaryPage';
import { useTheme } from '../arrangement/ThemeContext';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
console.log(GOOGLE_MAPS_API_KEY)


// Promise-based Google Maps loader
const testGooglePlacesAPI = () => {
  if (window.google && window.google.maps && window.google.maps.places) {
    console.log('Google Places API is loaded and available');
    
    // Test Places Service
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    console.log('Places Service created:', service);
    
    return true;
  } else {
    console.error('Google Places API not properly loaded');
    console.log('window.google:', window.google);
    console.log('window.google.maps:', window.google?.maps);
    console.log('window.google.maps.places:', window.google?.maps?.places);
    return false;
  }
};

// 5. Enhanced Google Maps loading with better error handling
const loadGoogleMapsAPI = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google?.maps?.places) {
      console.log('Google Maps API already loaded');
      resolve(window.google.maps);
      return;
    }

    // Check for existing promise
    if (window.googleMapsPromise) {
      window.googleMapsPromise.then(resolve).catch(reject);
      return;
    }

    window.googleMapsPromise = new Promise((resolvePromise, rejectPromise) => {
      // Remove any existing script
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existingScript && !window.google?.maps?.places) {
        existingScript.remove();
      }

      // Create new script with places library explicitly included
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      // Global callback function
      window.initGoogleMaps = () => {
        if (window.google?.maps?.places) {
          console.log('Google Maps API loaded successfully with Places');
          resolvePromise(window.google.maps);
        } else {
          console.error('Google Maps loaded but Places library missing');
          rejectPromise(new Error('Google Maps Places library not available'));
        }
        delete window.initGoogleMaps;
      };
      
      script.onerror = (error) => {
        console.error('Failed to load Google Maps script:', error);
        rejectPromise(new Error('Failed to load Google Maps API'));
        delete window.initGoogleMaps;
      };
      
      document.head.appendChild(script);
      
      // Timeout fallback
      setTimeout(() => {
        if (!window.google?.maps?.places) {
          rejectPromise(new Error('Google Maps API loading timeout'));
          delete window.initGoogleMaps;
        }
      }, 15000);
    });

    window.googleMapsPromise.then(resolve).catch(reject);
  });

};

export const FindNearbyModal = ({ isOpen, onClose, onDispensarySelect, url }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [showUserLocation, setShowUserLocation] = useState(true);
  const [isManualLocation, setIsManualLocation] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const { colors } = useTheme();

  const [nearbyDispensaries, setNearbyDispensaries] = useState([]);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isLoadingDispensaries, setIsLoadingDispensaries] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDispensary, setSelectedDispensary] = useState(null);
  const [searchRadius, setSearchRadius] = useState(10);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState('map');

  const [isDispensaryDetailOpen, setIsDispensaryDetailOpen] = useState(false);
  const [selectedDispensaryId, setSelectedDispensaryId] = useState(null);

  const getDispensaryIcon = (isSelected = false) => {
    return {
        url: url,
        scaledSize: new window.google.maps.Size(isSelected ? 70 : 60, isSelected ? 70 : 60),
        anchor: new window.google.maps.Point(isSelected ? 50 : 45, isSelected ? 70 : 60),
        origin: new window.google.maps.Point(0, 0)
    };
  };
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);

  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load Google Maps API
  useEffect(() => {
    let isMounted = true;
    
    loadGoogleMapsAPI()
      .then(() => {
        if (isMounted) {
          setIsMapLoaded(true);
        }
      })
      .catch((error) => {
        console.error('Error loading Google Maps:', error);
        if (isMounted) {
          setError('Failed to load map. Please refresh and try again.');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize map when modal opens
  useEffect(() => {
    if (isOpen && isMapLoaded && mapRef.current) {
      const timer = setTimeout(() => {
        if (!mapInstanceRef.current) {
          try {
            initializeMap();
          } catch (error) {
            console.error('Error initializing map:', error);
            setError('Failed to initialize map. Please try again.');
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, isMapLoaded, mobileView]);

  
  useEffect(() => {
  if (isMapLoaded && searchInputRef.current && isOpen && mapInstanceRef.current) {
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    const timer = setTimeout(() => {
      try {
        if (!searchInputRef.current || !mapInstanceRef.current) return;

        const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
          types: ['establishment'],
          fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components']
        });
        
        autocomplete.bindTo('bounds', mapInstanceRef.current);
        autocompleteRef.current = autocomplete;

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            
            setUserLocation({ lat, lng });
            setIsManualLocation(true);
            setLocationSearchQuery(place.formatted_address || place.name || '');
            
            if (mapInstanceRef.current) {
              mapInstanceRef.current.setCenter({ lat, lng });
              mapInstanceRef.current.setZoom(16);
              updateUserLocationMarker({ lat, lng });
            }
          }
        });

      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }
}, [isMapLoaded, isOpen, mapInstanceRef.current]);

  // Get user location when modal opens
  useEffect(() => {
    if (isOpen && !userLocation && !isManualLocation) {
      getCurrentLocation();
    }
  }, [isOpen]);

  // Fetch nearby dispensaries when user location changes
  useEffect(() => {
    if (userLocation) {
      fetchNearbyDispensaries();
    }
  }, [userLocation, searchRadius]);

  // Update user location marker visibility
  useEffect(() => {
    if (userMarkerRef.current) {
      userMarkerRef.current.setVisible(showUserLocation);
    }
  }, [showUserLocation]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const fetchNearbyDispensaries = async () => {
    if (!userLocation) return;
    
    setIsLoadingDispensaries(true);
    setError(null);
    
    try {
      const url = `${API_URL}/api/dispensaries?lat=${userLocation.lat}&lng=${userLocation.lng}&radius=${searchRadius}&limit=50`;
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dispensaries');
      }

      const data = await response.json();
      
      // Backend already filtered and calculated distances - just add display data
      const dispensariesWithExtras = data.dispensaries.map(dispensary => ({
        ...dispensary,
        rating: 4.0 + Math.random() * 1.0,
        reviewCount: Math.floor(Math.random() * 200) + 20,
        hours: "9AM - 9PM",
        phone: "(415) 555-" + String(Math.floor(Math.random() * 9000) + 1000)
      }));

      setNearbyDispensaries(dispensariesWithExtras);
      
      if (mapInstanceRef.current) {
        updateMapMarkers(dispensariesWithExtras);
      }
      
    } catch (err) {
      setError(err.message);
      console.error('Error fetching dispensaries:', err);
    } finally {
      setIsLoadingDispensaries(false);
    }
  };

  const initializeMap = () => {
    if (!window.google?.maps || !mapRef.current) {
      console.error('Google Maps API not loaded or map ref not available');
      return;
    }

    try {
      const defaultCenter = { lat: 42.3646296, lng: -71.0615132 };
      
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 12,
        center: defaultCenter,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            "featureType": "all",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#1a1a1a"}]
          },
          {
            "featureType": "all", 
            "elementType": "labels.text.fill",
            "stylers": [{"color": "#e5e5e5"}]
          },
          {
            "featureType": "water",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#0a0a0a"}]
          },
          {
            "featureType": "road",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#333"}]
          }
        ]
      });

      console.log('Map initialized successfully');
    } catch (error) {
      console.error('Error creating map instance:', error);
      setError('Failed to create map. Please refresh and try again.');
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setIsLoadingLocation(true);
    setError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        setUserLocation(location);
        setIsManualLocation(false);
        
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(location);
          mapInstanceRef.current.setZoom(13);
          updateUserLocationMarker(location);
        }
        
        setIsLoadingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setError('Unable to get your current location. Please enable location services or search for a location manually.');
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const updateUserLocationMarker = (location) => {
    if (!mapInstanceRef.current) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
    }
    
    userMarkerRef.current = new window.google.maps.Marker({
      position: location,
      map: mapInstanceRef.current,
      title: isManualLocation ? 'Selected Location' : 'Your Location',
      visible: showUserLocation,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: isManualLocation ? '#f59e0b' : '#00d4aa',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3
      }
    });
  };

  const updateMapMarkers = (dispensaries) => {
    if (!mapInstanceRef.current) return;

    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    dispensaries.forEach((dispensary, index) => {
      const marker = new window.google.maps.Marker({
        position: {
          lat: parseFloat(dispensary.latitude),
          lng: parseFloat(dispensary.longitude)
        },
        map: mapInstanceRef.current,
        title: dispensary.name,
        icon: getDispensaryIcon(selectedDispensary?.id === dispensary.id)
      });

      marker.addListener('click', () => {
        setSelectedDispensary(dispensary);
        marker.setIcon(getDispensaryIcon(true));
        
        markersRef.current.forEach(m => {
          if (m !== marker) {
            m.setIcon(getDispensaryIcon(false));
          }
        });
      });

      markersRef.current.push(marker);
    });

    if (dispensaries.length > 0 && userLocation && showUserLocation) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(userLocation);
      dispensaries.forEach(dispensary => {
        bounds.extend({
          lat: parseFloat(dispensary.latitude),
          lng: parseFloat(dispensary.longitude)
        });
      });
      mapInstanceRef.current.fitBounds(bounds);
    }
  };

  const handleDispensarySelect = (dispensary) => {
    setSelectedDispensary(dispensary);
    
    if (mapInstanceRef.current) {
        const marker = markersRef.current.find(m => 
        m.getTitle() === dispensary.name
        );
        if (marker) {
        marker.setIcon(getDispensaryIcon(true));
        
        markersRef.current.forEach(m => {
            if (m !== marker) {
            m.setIcon(getDispensaryIcon(false));
            }
        });
        
        mapInstanceRef.current.setCenter({
            lat: parseFloat(dispensary.latitude),
            lng: parseFloat(dispensary.longitude)
        });
        }
    }
  };
  
  
  const resetToCurrentLocation = () => {
    setIsLoadingLocation(true);
    setError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        // Reset all location-related states
        setUserLocation(location);
        setIsManualLocation(false);
        setLocationSearchQuery('');
        setShowUserLocation(true);
        
        // Clear the search input
        if (searchInputRef.current) {
          searchInputRef.current.value = '';
        }
        
        // Update map view
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(location);
          mapInstanceRef.current.setZoom(13);
          updateUserLocationMarker(location);
        }
        
        setIsLoadingLocation(false);
      },
      (error) => {
        console.error('Error getting current location:', error);
        setError('Unable to get your current location. Please check your location settings.');
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };


  const handleClose = () => {
    if (markersRef.current) {
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
    }
    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
      userMarkerRef.current = null;
    }
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }
    if (mapInstanceRef.current) {
      mapInstanceRef.current = null;
    }
    
    // ONLY clear the input value - don't create new autocomplete here!
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    
    setUserLocation(null);
    setNearbyDispensaries([]);
    setSelectedDispensary(null);
    setError(null);
    setIsDispensaryDetailOpen(false);
    setSelectedDispensaryId(null);
    setIsManualLocation(false);
    setLocationSearchQuery('');
    setShowUserLocation(true);
    onClose();
  };

  const getAddressComponent = (place, type) => {
    const component = place.address_components?.find(comp => comp.types.includes(type));
    return component ? component.long_name : '';
  };

  const handleSelectDispensary = () => {
    if (selectedDispensary && onDispensarySelect) {
        onDispensarySelect(selectedDispensary);
        setSelectedDispensaryId(selectedDispensary.id);
        setIsDispensaryDetailOpen(true);
    }
  };

  const toggleUserLocationVisibility = () => {
    setShowUserLocation(!showUserLocation);
  };



  if (!isOpen) return null;

  // Location Controls Component - FIXED VERSION
  const LocationControls = ({ isCompact = false }) => (
  <div style={{
    padding: isCompact ? '8px' : '12px',
    background: colors.bgCard,
    borderBottom: `1px solid ${colors.border}`,
    borderRadius: isCompact ? '8px' : '0'
  }}>
    {/* Location Search */}
    <div style={{ marginBottom: isCompact ? '8px' : '12px' }}>
      <label style={{
        display: 'block',
        marginBottom: '6px',
        fontWeight: '500',
        color: '#e5e5e5',
        fontSize: isCompact ? '12px' : '14px'
      }}>
        {isManualLocation ? 'Custom Location' : 'Search Location'}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          ref={searchInputRef}
          type="text"
          onChange={(e) => console.log('Input changed:', e.target.value)} 
          placeholder="Search for dispensary or address..."
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#2a2a2a',
            border: '2px solid #333',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            color: '#e5e5e5',
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.2s ease',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.currentTarget.style.borderColor = '#00d4aa'}
          onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
        />
                
        <Search 
          size={isCompact ? 12 : 14} 
          style={{ 
            position: 'absolute', 
            right: '8px', 
            top: '50%', 
            transform: 'translateY(-50%)', 
            color: isManualLocation ? '#00d4aa' : '#888'
          }} 
        />
      </div>
    </div>

    {/* SELECT DISPENSARY BUTTON - MOBILE ONLY */}
    {selectedDispensary && isCompact && (
      <div style={{ marginBottom: '12px' }}>
        <button
          onClick={handleSelectDispensary}
          style={{
            width: '100%',
            padding: '14px',
            background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
            color: '#0a0a0a',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'transform 0.1s ease'
          }}
          onTouchStart={(e) => {
            e.currentTarget.style.transform = 'scale(0.98)';
          }}
          onTouchEnd={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <Store size={16} />
          Select {selectedDispensary.name}
        </button>
      </div>
    )}
    
    {/* Location Controls Row */}
    <div style={{
      display: 'flex',
      gap: isCompact ? '6px' : '8px',
      alignItems: 'center'
    }}>
      <button
        onClick={getCurrentLocation}
        disabled={isLoadingLocation}
        style={{
          flex: 1,
          padding: isCompact ? '6px 8px' : '8px 12px',
          background: isLoadingLocation ? '#333' : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
          color: isLoadingLocation ? '#888' : '#0a0a0a',
          border: 'none',
          borderRadius: '6px',
          fontSize: isCompact ? '10px' : '12px',
          fontWeight: '600',
          cursor: isLoadingLocation ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px'
        }}
      >
        {isLoadingLocation ? (
          <Loader size={isCompact ? 10 : 12} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <Target size={isCompact ? 10 : 12} />
        )}
        Use GPS
      </button>

      {/* RESET BUTTON - Only show if using manual location */}
      {isManualLocation && (
        <button
          onClick={resetToCurrentLocation}
          disabled={isLoadingLocation}
          style={{
            padding: isCompact ? '6px 8px' : '8px 12px',
            background: isLoadingLocation ? '#333' : '#f59e0b',
            color: isLoadingLocation ? '#888' : '#0a0a0a',
            border: 'none',
            borderRadius: '6px',
            fontSize: isCompact ? '10px' : '12px',
            fontWeight: '600',
            cursor: isLoadingLocation ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            whiteSpace: 'nowrap'
          }}
          title="Reset to your current location"
        >
          {isLoadingLocation ? (
            <Loader size={isCompact ? 10 : 12} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Navigation size={isCompact ? 10 : 12} />
          )}
          Reset
        </button>
      )}

      <button
        onClick={toggleUserLocationVisibility}
        style={{
          padding: isCompact ? '6px' : '8px',
          background: showUserLocation ? '#333' : '#1a1a1a',
          border: `1px solid ${showUserLocation ? '#00d4aa' : '#333'}`,
          borderRadius: '6px',
          color: showUserLocation ? '#00d4aa' : '#888',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        title={showUserLocation ? 'Hide location marker' : 'Show location marker'}
      >
        {showUserLocation ? (
          <Eye size={isCompact ? 12 : 14} />
        ) : (
          <EyeOff size={isCompact ? 12 : 14} />
        )}
      </button>
    </div>

    {isManualLocation && (
      <div style={{
        marginTop: isCompact ? '6px' : '8px',
        fontSize: isCompact ? '10px' : '11px',
        color: '#f59e0b',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <MapPin size={isCompact ? 10 : 12} />
        Using custom location: {locationSearchQuery}
      </div>
    )}
  </div>
);

  // Mobile Layout (unchanged for now)
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Mobile Header */}
        <div style={{
          padding: '16px',
          background:colors.bgGradient,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: '#e5e5e5',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Navigation style={{ color: '#00d4aa' }} size={20} />
              Find Nearby
            </h2>
            <p style={{
              margin: '2px 0 0 28px',
              color: '#888',
              fontSize: '12px'
            }}>
              {nearbyDispensaries.length} dispensaries found
            </p>
          </div>
          
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Mobile Location Controls */}
        <LocationControls isCompact={true} />

        {/* Mobile Toggle Buttons */}
        <div style={{
          display: 'flex',
          background: colors.bgTertiary,
          borderBottom: '1px solid #333'
        }}>
          <button
            onClick={() => setMobileView('map')}
            style={{
              flex: 1,
              padding: '12px',
              background: mobileView === 'map' ? '#00d4aa' : 'transparent',
              color: mobileView === 'map' ? '#0a0a0a' : '#888',
              border: 'none',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: 'pointer'
            }}
          >
            <Map size={16} />
            Map View
          </button>
          <button
            onClick={() => setMobileView('list')}
            style={{
              flex: 1,
              padding: '12px',
              background: mobileView === 'list' ? '#00d4aa' : 'transparent',
              color: mobileView === 'list' ? '#0a0a0a' : '#888',
              border: 'none',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: 'pointer'
            }}
          >
            <List size={16} />
            List View
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderBottom: `1px solid ${colors.border}`,
            color: '#ef4444',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Mobile Content */}
        <div style={{ flex: 1, overflow: 'hidden', background: colors.bgTertiary, }}>
          {mobileView === 'map' ? (
            // Map View
            <div style={{ position: 'relative', height: '100%' }}>
              {!isMapLoaded ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#888'
                }}>
                  <Loader size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                  Loading map...
                </div>
              ) : (
                <>
                  <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                  
                  {/* Enhanced Map Legend */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '8px',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }}>
                    {showUserLocation && userLocation && (
                      <div style={{ 
                        color: isManualLocation ? '#f59e0b' : '#00d4aa', 
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        ● {isManualLocation ? 'Custom Location' : 'Your Location'}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <img src={url} style={{ width: '12px', height: '12px' }} alt="dispensary" />
                      Dispensaries
                    </div>
                  </div>

                  {/* Floating Search Radius */}
                  <div style={{
                    position: 'absolute',
                    bottom: '16px',
                    left: '16px',
                    right: '16px',
                    background: 'rgba(26, 26, 26, 0.95)',
                    padding: '12px',
                    borderRadius: '12px',
                    border: '1px solid #333'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: '500' }}>
                        Search Radius: {searchRadius} miles
                      </span>
                      <span style={{ color: '#888', fontSize: '12px' }}>
                        {nearbyDispensaries.length} found
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="25"
                      value={searchRadius}
                      onChange={(e) => setSearchRadius(Number(e.target.value))}
                      style={{
                        width: '100%',
                        accentColor: '#00d4aa'
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            // List View - Split View with Map on Top
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Map Section (Top Half) */}
              <div style={{ height: '50%', position: 'relative', borderBottom: '1px solid #333' }}>
                {!isMapLoaded ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#888'
                  }}>
                    <Loader size={20} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                    Loading map...
                  </div>
                ) : (
                  <>
                    <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                    
                    {/* Compact Map Legend */}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      padding: '6px',
                      borderRadius: '6px',
                      fontSize: '10px'
                    }}>
                      {showUserLocation && userLocation && (
                        <div style={{ 
                          color: isManualLocation ? '#f59e0b' : '#00d4aa', 
                          marginBottom: '2px' 
                        }}>
                          ● {isManualLocation ? 'Custom' : 'You'}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <img src={url} style={{ width: '10px', height: '10px' }} alt="dispensary" />
                        Shops
                      </div>
                    </div>

                    {/* Compact Search Radius */}
                    <div style={{
                      position: 'absolute',
                      bottom: '8px',
                      left: '8px',
                      right: '8px',
                      background: 'rgba(26, 26, 26, 0.95)',
                      padding: '8px',
                      borderRadius: '8px',
                      border: '1px solid #333'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '4px'
                      }}>
                        <span style={{ color: '#e5e5e5', fontSize: '12px', fontWeight: '500' }}>
                          {searchRadius} miles
                        </span>
                        <span style={{ color: '#888', fontSize: '11px' }}>
                          {nearbyDispensaries.length} found
                        </span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="25"
                        value={searchRadius}
                        onChange={(e) => setSearchRadius(Number(e.target.value))}
                        style={{
                          width: '100%',
                          accentColor: '#00d4aa',
                          height: '4px'
                        }}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Results Section (Bottom Half) */}
              <div style={{ height: '50%', display: 'flex', flexDirection: 'column' }}>
                {/* Results Header */}
                <div style={{
                  padding: '12px 16px',
                  background: colors.bgCard,
                  borderBottom: `1px solid ${colors.border}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: '600' }}>
                    Nearby Dispensaries
                  </span>
                  <span style={{ color: '#888', fontSize: '12px' }}>
                    {nearbyDispensaries.length} results
                  </span>
                </div>

                {/* Loading State */}
                {(isLoadingLocation || isLoadingDispensaries) && (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#888',
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Loader size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: '8px', color: '#00d4aa' }} />
                    <p style={{ margin: 0, fontSize: '12px' }}>
                      {isLoadingLocation ? 'Getting location...' : 'Finding dispensaries...'}
                    </p>
                  </div>
                )}

                {/* Dispensary List - Scrollable */}
                {!isLoadingLocation && !isLoadingDispensaries && (
                  <div style={{ 
                    flex: 1, 
                    overflowY: 'auto', 
                    padding: '8px 12px'
                  }}>
                    {nearbyDispensaries.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        color: '#888'
                      }}>
                        <Store size={24} style={{ marginBottom: '8px' }} />
                        <p style={{ margin: 0, fontSize: '12px' }}>
                          No dispensaries found within {searchRadius} miles
                        </p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {nearbyDispensaries.map((dispensary) => (
                          <div
                            key={dispensary.id}
                            onClick={() => handleDispensarySelect(dispensary)}
                            style={{
                              padding: '12px',
                              background: selectedDispensary?.id === dispensary.id 
                                ? 'linear-gradient(135deg, rgba(0, 212, 170, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)'
                                : '#2a2a2a',
                              border: selectedDispensary?.id === dispensary.id 
                                ? '2px solid #00d4aa' 
                                : '1px solid #333',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '6px'
                            }}>
                              <h3 style={{
                                margin: 0,
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#e5e5e5',
                                lineHeight: '1.2'
                              }}>
                                {dispensary.name}
                              </h3>
                              <div style={{
                                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                                color: '#0a0a0a',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                flexShrink: 0,
                                marginLeft: '8px'
                              }}>
                                {dispensary.distance.toFixed(1)} mi
                              </div>
                            </div>

                            <p style={{
                              margin: '0 0 6px 0',
                              fontSize: '11px',
                              color: '#888',
                              lineHeight: '1.3'
                            }}>
                              {dispensary.address && `${dispensary.address}, `}
                              {dispensary.city}, {dispensary.state}
                            </p>

                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <Star size={10} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                                <span style={{ fontSize: '11px', color: '#e5e5e5', fontWeight: '500' }}>
                                  {dispensary.rating.toFixed(1)}
                                </span>
                                <span style={{ fontSize: '10px', color: '#888' }}>
                                  ({dispensary.reviewCount})
                                </span>
                              </div>
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <Clock size={10} style={{ color: '#888' }} />
                                <span style={{ fontSize: '10px', color: '#888' }}>
                                  {dispensary.hours}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DispensaryDetailModal
          isOpen={isDispensaryDetailOpen}
          onClose={() => setIsDispensaryDetailOpen(false)}
          dispensaryId={selectedDispensaryId}
        />

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Desktop Layout - FIXED VERSION
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${colors.border}`,
          background:colors.bgGradient,

          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '700',
              color: '#e5e5e5',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Navigation style={{ color: '#00d4aa' }} size={24} />
              Find Nearby Dispensaries
            </h2>
            <p style={{
              margin: '4px 0 0 36px',
              color: '#888',
              fontSize: '14px'
            }}>
              {userLocation 
                ? `Found ${nearbyDispensaries.length} dispensaries within ${searchRadius} miles`
                : 'Getting your location to find nearby dispensaries...'
              }
            </p>
          </div>
          
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#888';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            padding: '16px 24px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderBottom: `1px solid ${colors.border}`,
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <X size={16} />
            {error}
            <button
              onClick={getCurrentLocation}
              style={{
                marginLeft: 'auto',
                padding: '6px 12px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Content - FIXED LAYOUT */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '400px 1fr',
          height: 'calc(90vh - 140px)',
          overflow: 'hidden'
        }}>
          {/* Left Panel - Dispensary List - FIXED SCROLLING */}
          <div style={{
            borderRight: '1px solid #333',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0  // Important for flex containers
          }}>
            {/* Location Controls - FIXED */}
            <div style={{
              flexShrink: 0  // Prevent shrinking
            }}>
              <LocationControls />
            </div>

            {/* Search Radius Control - FIXED */}
            <div style={{
              padding: '20px',
              borderBottom: `1px solid ${colors.border}`,
              background: colors.bgCard,
              flexShrink: 0  // Prevent shrinking
            }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#e5e5e5',
                fontSize: '14px'
              }}>
                Search Radius: {searchRadius} miles
              </label>
              <input
                type="range"
                min="1"
                max="25"
                value={searchRadius}
                onChange={(e) => setSearchRadius(Number(e.target.value))}
                style={{
                  width: '100%',
                  accentColor: '#00d4aa'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#888',
                marginTop: '4px'
              }}>
                <span>1 mile</span>
                <span>25 miles</span>
              </div>
            </div>

            {/* Select Button (when dispensary selected) - FIXED */}
            {selectedDispensary && (
              <div style={{
                padding: '16px',
                borderBottom: `1px solid ${colors.border}`,
                background: colors.bgCard,
                flexShrink: 0  // Prevent shrinking
              }}>
                <button
                  onClick={handleSelectDispensary}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                    color: '#0a0a0a',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Store size={16} />
                  Select {selectedDispensary.name}
                </button>
              </div>
            )}

            {/* Loading States */}
            {(isLoadingLocation || isLoadingDispensaries) && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#888',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
              }}>
                <Loader size={24} style={{
                  animation: 'spin 1s linear infinite',
                  marginBottom: '12px',
                  color: '#00d4aa'
                }} />
                <p style={{ margin: 0, fontSize: '14px' }}>
                  {isLoadingLocation ? 'Getting your location...' : 'Finding nearby dispensaries...'}
                </p>
              </div>
            )}

            {/* Dispensary List - FIXED SCROLLING */}
            {!isLoadingLocation && !isLoadingDispensaries && (
              <div style={{
                flex: 1,
                minHeight: 0,  // Important for scrolling
                overflowY: 'auto',
                padding: '16px'
              }}>
                {nearbyDispensaries.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#888'
                  }}>
                    <Store size={32} style={{ marginBottom: '12px' }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>
                      No dispensaries found within {searchRadius} miles
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {nearbyDispensaries.map((dispensary) => (
                      <div
                        key={dispensary.id}
                        onClick={() => handleDispensarySelect(dispensary)}
                        style={{
                          padding: '16px',
                          background: selectedDispensary?.id === dispensary.id 
                            ? 'linear-gradient(135deg, rgba(0, 212, 170, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)'
                            : colors.bgCard,
                          border: selectedDispensary?.id === dispensary.id 
                            ? '2px solid #00d4aa' 
                            : '1px solid #333',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedDispensary?.id !== dispensary.id) {
                            e.currentTarget.style.background = '#333';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedDispensary?.id !== dispensary.id) {
                            e.currentTarget.style.background = colors.bgCard;
                          }
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '8px'
                        }}>
                          <h3 style={{
                            margin: 0,
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#e5e5e5'
                          }}>
                            {dispensary.name}
                          </h3>
                          <div style={{
                            background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                            color: '#0a0a0a',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {dispensary.distance.toFixed(1)} mi
                          </div>
                        </div>

                        <p style={{
                          margin: '0 0 8px 0',
                          fontSize: '13px',
                          color: '#888',
                          lineHeight: '1.4'
                        }}>
                          {dispensary.address && `${dispensary.address}, `}
                          {dispensary.city}, {dispensary.state}
                        </p>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Star size={12} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                            <span style={{ fontSize: '12px', color: '#e5e5e5', fontWeight: '500' }}>
                              {dispensary.rating.toFixed(1)}
                            </span>
                            <span style={{ fontSize: '11px', color: '#888' }}>
                              ({dispensary.reviewCount})
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={12} style={{ color: '#888' }} />
                            <span style={{ fontSize: '11px', color: '#888' }}>
                              {dispensary.hours}
                            </span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                          {dispensary.website && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Globe size={10} style={{ color: '#888' }} />
                              <span style={{ fontSize: '10px', color: '#888' }}>
                                Website
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Map */}
          <div style={{ position: 'relative', background: '#2a2a2a' }}>
            {!isMapLoaded ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#888'
              }}>
                <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ marginLeft: '12px' }}>Loading map...</span>
              </div>
            ) : (
              <>
                <div
                  ref={mapRef}
                  style={{
                    width: '100%',
                    height: '100%'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  {showUserLocation && userLocation && (
                    <div style={{ 
                      color: isManualLocation ? '#f59e0b' : '#00d4aa', 
                      marginBottom: '4px' 
                    }}>
                      ● {isManualLocation ? 'Custom Location' : 'Your Location'}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <img 
                      src={url}
                      style={{ width: '16px', height: '16px' }} 
                      alt="dispensary"
                    />
                    Dispensaries
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      <DispensaryDetailModal
        isOpen={isDispensaryDetailOpen}
        onClose={() => setIsDispensaryDetailOpen(false)}
        dispensaryId={selectedDispensaryId}
      />

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* FORCE GOOGLE AUTOCOMPLETE DROPDOWN TO SHOW */
        .pac-container {
          display: block !important;
          background-color: #2a2a2a !important;
          border: 1px solid #333 !important;
          border-radius: 8px !important;
          z-index: 100001 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5) !important;
        }
        
        .pac-item {
          background-color: #2a2a2a !important;
          color: #e5e5e5 !important;
          border-top: 1px solid #333 !important;
          padding: 8px 12px !important;
          cursor: pointer !important;
          font-size: 14px !important;
        }
        
        .pac-item:hover {
          background-color: #00d4aa !important;
          color: #0a0a0a !important;
        }
        
        .pac-item:first-child {
          border-top: none !important;
        }
        
        .pac-item-query {
          color: #e5e5e5 !important;
          font-size: 14px !important;
        }
        
        .pac-matched {
          font-weight: bold !important;
          color: #00d4aa !important;
        }
        
        /* Custom scrollbar for desktop */
        .dispensary-list::-webkit-scrollbar {
          width: 6px;
        }
        
        .dispensary-list::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        
        .dispensary-list::-webkit-scrollbar-thumb {
          background: #00d4aa;
          border-radius: 3px;
        }
        
        .dispensary-list::-webkit-scrollbar-thumb:hover {
          background: #10b981;
        }
      `}</style>
    </div>
  );
};